<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { uploadEventLogo, deleteEventLogo } from '@/api/registration'
import AppSpinner from '@/components/shared/AppSpinner.vue'

const props = defineProps<{
  eventSlug: string
  currentLogoUrl: string | null
}>()

const emit = defineEmits<{
  'logo-updated': [logoUrl: string | null]
}>()

const { t } = useI18n()

const fileInput = ref<HTMLInputElement | null>(null)
const uploading = ref(false)
const removing = ref(false)
const uploadError = ref('')

function isAxiosError(
  e: unknown,
): e is { response: { status: number; data: { message?: string; errors?: Record<string, string[]> } } } {
  return (
    typeof e === 'object' &&
    e !== null &&
    'response' in e &&
    typeof (e as { response: unknown }).response === 'object' &&
    (e as { response: unknown }).response !== null
  )
}

function triggerFileInput(): void {
  fileInput.value?.click()
}

async function handleFileChange(event: Event): Promise<void> {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file) return

  uploadError.value = ''
  uploading.value = true

  try {
    const result = await uploadEventLogo(props.eventSlug, file)
    emit('logo-updated', result.logo_url)
  } catch (e: unknown) {
    if (isAxiosError(e) && e.response.status === 422) {
      const errors = e.response.data.errors
      if (errors?.logo) {
        uploadError.value = errors.logo[0]
      } else {
        uploadError.value = t('common.error_generic')
      }
    } else {
      uploadError.value = t('common.error_generic')
    }
  } finally {
    uploading.value = false
    // Reset input so the same file can be re-selected if needed
    if (fileInput.value) fileInput.value.value = ''
  }
}

async function handleRemove(): Promise<void> {
  uploadError.value = ''
  removing.value = true
  try {
    await deleteEventLogo(props.eventSlug)
    emit('logo-updated', null)
  } catch {
    uploadError.value = t('common.error_generic')
  } finally {
    removing.value = false
  }
}
</script>

<template>
  <div>
    <label class="block text-sm font-medium text-slate-300 mb-2">
      {{ t('events.form.logo.upload_logo_label') }}
    </label>

    <div class="flex items-start gap-4">
      <!-- Current logo preview -->
      <div
        class="w-20 h-20 rounded-lg border border-slate-700 overflow-hidden shrink-0 bg-slate-800 flex items-center justify-center"
      >
        <img
          v-if="currentLogoUrl"
          :src="currentLogoUrl"
          alt="Event logo"
          class="w-full h-full object-cover"
        />
        <svg
          v-else
          class="w-8 h-8 text-slate-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      </div>

      <div class="flex-1 space-y-2">
        <div class="flex flex-wrap gap-2">
          <!-- Upload button -->
          <button
            type="button"
            :disabled="uploading"
            class="inline-flex items-center gap-1.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed text-slate-200 text-sm font-medium rounded-lg px-3 py-2 transition-colors"
            @click="triggerFileInput"
          >
            <AppSpinner v-if="uploading" size="button" />
            <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            {{ uploading ? t('common.loading') : t('events.form.logo.upload_logo_label') }}
          </button>

          <!-- Remove button (only when logo exists) -->
          <button
            v-if="currentLogoUrl"
            type="button"
            :disabled="removing"
            class="inline-flex items-center gap-1.5 border border-red-500/40 hover:border-red-500 text-red-400 hover:text-red-300 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium rounded-lg px-3 py-2 transition-colors"
            @click="handleRemove"
          >
            <AppSpinner v-if="removing" size="button" />
            {{ removing ? t('common.loading') : t('events.form.logo.remove_logo_label') }}
          </button>
        </div>

        <p class="text-xs text-slate-500">{{ t('events.form.logo.logo_hint') }}</p>

        <p v-if="uploadError" class="text-xs text-red-400">{{ uploadError }}</p>
      </div>
    </div>

    <!-- Hidden file input -->
    <input
      ref="fileInput"
      type="file"
      accept="image/png,image/jpeg"
      class="hidden"
      @change="handleFileChange"
    />
  </div>
</template>
